import {faPaperclip, faUpload, faXmark} from '@fortawesome/pro-regular-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Form, FormInstance, Input, Tag, Upload} from 'antd';
import {RcFile} from 'antd/lib/upload';
import {UploadChangeParam, UploadFile} from 'antd/lib/upload/interface';

import {Text} from '../../../../../../../../../component/text/text';
import {useDomainConfig} from '../../../../../../../../../provider/domain-config/domain-config-hook';
import {useLocale} from '../../../../../../../../../provider/locale/locale-hook';
import {Locale} from '../../../../../../../../../provider/locale/localization';
import {useFormRules} from '../../../../../../../../../service/form/form-rules-hook';
import {classNames} from '../../../../../../../../../util/css';

import {AppstoreApiKeyFormEnum, AppstoreApiKeyFormType} from './appstore-api-key-form-type';
import * as styles from './appstore-api-key-form.scss';

const INSTRUCTION_LINK =
    'https://developer.apple.com/documentation/appstoreconnectapi/creating_api_keys_for_app_store_connect_api';

const ROLE_CHIPS = ['Customer Support', 'Admin', 'Account Holder'];

function getPrivateKeyUploadFileList(file?: File): Array<UploadFile> {
    if (!file) {
        return [];
    }

    return [
        {
            uid: file.name,
            name: file.name,
            status: 'done',
            originFileObj: file as RcFile,
        },
    ];
}

function getPrivateKeyFromUploadEvent(event: UploadChangeParam): File {
    return event.file.originFileObj ?? (event.file as RcFile);
}

type PropsType = {
    form: FormInstance<AppstoreApiKeyFormType>;
    formId: string;
    onFinish: (values: AppstoreApiKeyFormType) => void;
};

export function AppstoreApiKeyForm(props: PropsType): JSX.Element {
    const {form, formId, onFinish} = props;
    const {requiredFieldRule, validAppstoreIssuerIdRule, validAppstoreKeyIdRule, validAppstorePrivateKeyRule} =
        useFormRules();
    const {companyName} = useDomainConfig();
    const {getLocalizedString} = useLocale();

    const orText = getLocalizedString('TEXT__OR').toLowerCase();

    const privateKeyValue = Form.useWatch(AppstoreApiKeyFormEnum.privateKey, form);

    function handleBeforeUpload(file: RcFile): boolean {
        form.setFieldValue(AppstoreApiKeyFormEnum.privateKey, file);
        form.validateFields([AppstoreApiKeyFormEnum.privateKey]);
        return false;
    }

    function handleRemovePrivateKey(): void {
        form.resetFields([AppstoreApiKeyFormEnum.privateKey]);
    }

    return (
        <Form<AppstoreApiKeyFormType> form={form} id={formId} layout="vertical" onFinish={onFinish}>
            <Text
                block
                className={styles.AppstoreApiKeyForm_description}
                lighter
                stringKey="SOURCE_SETTINGS__ACCOUNTS__APPSTORE__STEP1__DESCRIPTION"
                valueMap={{
                    companyName,
                    link: (
                        <a href="https://appstoreconnect.apple.com/login" rel="noreferrer" target="_blank">
                            App Store Connect
                        </a>
                    ),
                }}
            />

            <div className={styles.AppstoreApiKeyForm_rolesRow}>
                <span className={styles.AppstoreApiKeyForm_rolesText}>
                    <Locale stringKey="SOURCE_SETTINGS__ACCOUNTS__APPSTORE__ROLES__TEXT" />
                </span>
                {ROLE_CHIPS.map((role, index) => (
                    <span key={role}>
                        {index > 0 && (
                            <span className={styles.AppstoreApiKeyForm_rolesText}>
                                {index === ROLE_CHIPS.length - 1 ? ` ${orText} ` : ', '}
                            </span>
                        )}
                        <Tag className={styles.AppstoreApiKeyForm_roleTag}>{role}</Tag>
                    </span>
                ))}
            </div>

            <p className={styles.AppstoreApiKeyForm_instructionRow}>
                <Locale
                    stringKey="SOURCE_SETTINGS__ACCOUNTS__APPSTORE__HOWTO_TEXT"
                    valueMap={{
                        link: (
                            <a href={INSTRUCTION_LINK} rel="noreferrer" target="_blank">
                                <Locale stringKey="SOURCE_SETTINGS__ACCOUNTS__APPSTORE__INSTRUCTION_LINK" />
                            </a>
                        ),
                    }}
                />
            </p>

            <Form.Item<AppstoreApiKeyFormType>
                label={<Locale stringKey="SOURCE_SETTINGS__ACCOUNTS__APPSTORE__ISSUER_ID" />}
                name={AppstoreApiKeyFormEnum.issuerId}
                rules={[requiredFieldRule, validAppstoreIssuerIdRule()]}
                validateFirst
            >
                <Input placeholder="00000000-0000-0000-0000-000000000000" />
            </Form.Item>

            <Form.Item<AppstoreApiKeyFormType>
                className={styles.AppstoreApiKeyForm_keyIdItem}
                label={<Locale stringKey="SOURCE_SETTINGS__ACCOUNTS__APPSTORE__KEY_ID" />}
                name={AppstoreApiKeyFormEnum.keyId}
                rules={[requiredFieldRule, validAppstoreKeyIdRule()]}
                validateFirst
            >
                <Input placeholder="XXXXXXXXXX" />
            </Form.Item>

            <Form.Item<AppstoreApiKeyFormType>
                getValueFromEvent={getPrivateKeyFromUploadEvent}
                getValueProps={(file?: File) => ({
                    fileList: getPrivateKeyUploadFileList(file),
                })}
                label={<Locale stringKey="SOURCE_SETTINGS__ACCOUNTS__APPSTORE__PRIVATE_KEY" />}
                name={AppstoreApiKeyFormEnum.privateKey}
                rules={[requiredFieldRule, validAppstorePrivateKeyRule()]}
                validateFirst
            >
                <Upload
                    accept=".p8"
                    beforeUpload={handleBeforeUpload}
                    className={styles.AppstoreApiKeyForm_upload}
                    maxCount={1}
                    showUploadList={false}
                >
                    <div
                        className={classNames(
                            styles.AppstoreApiKeyForm_uploadArea,
                            form.getFieldError(AppstoreApiKeyFormEnum.privateKey).length > 0
                                ? styles.AppstoreApiKeyForm_uploadArea_error
                                : null
                        )}
                    >
                        <FontAwesomeIcon className={styles.AppstoreApiKeyForm_uploadIcon} icon={faUpload} />
                        <span className={styles.AppstoreApiKeyForm_uploadText}>
                            <Locale stringKey="SOURCE_SETTINGS__ACCOUNTS__APPSTORE__PRIVATE_KEY__UPLOAD" />
                        </span>
                    </div>
                </Upload>
            </Form.Item>

            {privateKeyValue && (
                <div className={styles.AppstoreApiKeyForm_fileName}>
                    <span className={styles.AppstoreApiKeyForm_fileNameMain}>
                        <FontAwesomeIcon icon={faPaperclip} />
                        <span>{privateKeyValue.name}</span>
                    </span>
                    <button
                        aria-label={getLocalizedString('BUTTON__DELETE')}
                        className={styles.AppstoreApiKeyForm_removeFileButton}
                        onClick={handleRemovePrivateKey}
                        type="button"
                    >
                        <FontAwesomeIcon icon={faXmark} />
                    </button>
                </div>
            )}
        </Form>
    );
}
